import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HiMenu } from 'react-icons/hi';
import { FaShoppingBag, FaChevronDown } from 'react-icons/fa';
import { motion } from 'framer-motion';
import MobileMenu from './MobileMenu';

function Header() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinkClass = (path) =>
    `font-medium border-b-2 pb-1 transition-colors ${
      location.pathname === path
        ? 'text-primary-600 border-primary-600'
        : 'text-navy-800 border-transparent hover:text-primary-600 hover:border-primary-200'
    }`;

  return (
    <>
      <header className="bg-white/95 backdrop-blur text-navy-900 shadow-sm border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 lg:py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
                src="/logo.png"
                alt="Marple Swimming Club Logo"
                className="h-14 lg:h-16 w-auto"
              />
              <h1 className="font-display text-xl lg:text-2xl font-bold hidden sm:block text-navy-900 group-hover:text-primary-600 transition-colors">
                Marple Swimming Club
              </h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              <ul className="flex items-center gap-6">
                <li>
                  <Link to="/" className={navLinkClass('/')}>
                    Home
                  </Link>
                </li>

                {/* The Club Dropdown */}
                <li className="relative group">
                  <button className="font-medium border-b-2 border-transparent pb-1 text-navy-800 hover:text-primary-600 hover:border-primary-200 transition-colors flex items-center gap-1.5">
                    The Club
                    <FaChevronDown className="w-3 h-3 group-hover:rotate-180 transition-transform" />
                  </button>

                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 mt-3 w-56 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform group-hover:translate-y-0 -translate-y-1 overflow-hidden">
                    <ul className="py-2">
                      <li>
                        <Link
                          to="/about"
                          className="block px-4 py-3 text-navy-800 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                        >
                          About The Club
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/competitive"
                          className="block px-4 py-3 text-navy-800 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                        >
                          Competitive Swimming
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/resources"
                          className="block px-4 py-3 text-navy-800 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                        >
                          Resources
                        </Link>
                      </li>
                    </ul>
                  </div>
                </li>

                <li>
                  <Link to="/programs" className={navLinkClass('/programs')}>
                    Programs
                  </Link>
                </li>
                <li>
                  <Link to="/committee" className={navLinkClass('/committee')}>
                    Committee
                  </Link>
                </li>
                <li>
                  <Link to="/events" className={navLinkClass('/events')}>
                    Events
                  </Link>
                </li>
                <li>
                  <Link to="/results" className={navLinkClass('/results')}>
                    Results
                  </Link>
                </li>
                <li>
                  <Link to="/new-pool" className={navLinkClass('/new-pool')}>
                    New Pool
                  </Link>
                </li>
                <li>
                  <Link to="/masters-interest" className={navLinkClass('/masters-interest')}>
                    Masters/Adults
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className={navLinkClass('/contact')}>
                    Contact
                  </Link>
                </li>
              </ul>

              {/* Shop Button */}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://pbteamwear.co.uk/collections/marple-sc"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-600 text-white px-5 py-2 rounded-full font-semibold hover:bg-primary-700 transition-all duration-300 flex items-center gap-2 shadow-sm"
              >
                <FaShoppingBag className="w-4 h-4" />
                Shop
              </motion.a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden text-navy-800 hover:text-primary-600 transition-colors p-2"
              aria-label="Open menu"
            >
              <HiMenu size={32} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>
  );
}

export default Header;
